import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../config/firebase';
import { ref, get } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../ui/Spinner';
import {
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentPage,
  StudentSection,
} from './StudentUI';

const StudentGrades = () => {
  const { currentUser } = useAuth();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const gradesRef = ref(db, `grades/${currentUser.uid}`);
        const snapshot = await get(gradesRef);
        if (snapshot.exists()) {
          setGrades(Object.values(snapshot.val()));
        } else {
          setGrades([]);
        }
      } catch (error) {
        console.error('Error fetching grades:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGrades();
  }, [currentUser.uid]);

  const stats = useMemo(() => {
    const numericScores = grades
      .map((grade) => Number(grade.score))
      .filter((score) => !Number.isNaN(score));
    const average =
      numericScores.length > 0
        ? Math.round(
            numericScores.reduce((sum, score) => sum + score, 0) /
              numericScores.length,
          )
        : 0;

    return {
      total: grades.length,
      average,
      highest: numericScores.length ? Math.max(...numericScores) : 0,
    };
  }, [grades]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <StudentPage
      title="Grades"
      subtitle="See your latest scores and feedback in one simple place."
    >
      <StudentSection title="Overview">
        <div className="grid gap-4 sm:grid-cols-3">
          <MetricCard label="Results" value={stats.total} />
          <MetricCard label="Average" value={stats.average || '-'} tone="success" />
          <MetricCard label="Highest" value={stats.highest || '-'} tone="accent" />
        </div>
      </StudentSection>

      <StudentSection title="Grade list">
        {!grades.length ? (
          <StudentEmpty
            title="No grades yet"
            message="Your scores will appear here after work is reviewed."
          />
        ) : (
          <div className="grid gap-4">
            {grades.map((grade) => (
              <StudentCard key={grade.id} className="bg-white">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-stone-900">
                        {grade.assignmentName || 'Assessment'}
                      </p>
                      <p className="mt-1 text-sm text-stone-500">
                        {grade.courseName || 'Course'}
                      </p>
                    </div>
                    <StudentBadge tone="success">{grade.score ?? '-'}</StudentBadge>
                  </div>
                  {grade.feedback ? (
                    <p className="text-sm leading-6 text-stone-600">
                      {grade.feedback}
                    </p>
                  ) : (
                    <p className="text-sm leading-6 text-stone-500">
                      No feedback added yet.
                    </p>
                  )}
                </div>
              </StudentCard>
            ))}
          </div>
        )}
      </StudentSection>
    </StudentPage>
  );
};

const MetricCard = ({ label, value, tone = 'soft' }) => (
  <StudentCard className="bg-white">
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
        {label}
      </p>
      <div className="flex items-center justify-between gap-3">
        <p className="text-2xl font-semibold text-stone-900">{value}</p>
        <StudentBadge tone={tone}>{label}</StudentBadge>
      </div>
    </div>
  </StudentCard>
);

export default StudentGrades;
